'use client';

import { Button, FieldRequiredMark, Text } from '@/shared/ui/core';
import { CheckIcon } from '@phosphor-icons/react/ssr';
import { BillingToggle } from '@/shared/ui/kit/billing-toggle';
import { PlanCard } from '@/shared/ui/kit/plan-card';
import { useState } from 'react';
import { usePlans } from '../lib/use-plans';
import type { PlanPickerValue } from '../model/plan-picker-value';
import styles from './plan-picker.module.scss';


type Props = {
    value   : PlanPickerValue;
    onChange: (next: PlanPickerValue) => void;
    error?  : string;
    teamEnabled?: boolean;
};


export const PlanPicker = ({ value, onChange, error, teamEnabled }: Props) => {
    const { plans } = usePlans();
    const [ openId, setOpenId ] = useState('');

    const setPlanId = (plan_id: string) => {
        if (value.plan_id === plan_id) return;
        onChange({ ...value, plan_id });
    };

    const setBillingPeriod: typeof onChange = (next) => onChange(next);

    return (
        <div
            className={ styles.root }
            data-template="plan-picker"
        >
            <BillingToggle
                value={ value.billing_period }
                onChange={ (billing_period) => setBillingPeriod({ ...value, billing_period }) }
                discountPercent={ 20 }
            />

            <div className={ styles.grid }>
                {
                    plans.map((plan) => {
                        const isSelected = value.plan_id === plan.id;
                        const noSelected = !value.plan_id;

                        return (
                            <PlanCard
                                key={ plan.id }
                                plan={ plan }
                                billingPeriod={ value.billing_period }
                                isSelected={ isSelected }
                                openId={ openId }
                                setOpenId={ setOpenId }
                                teamEnabled={ teamEnabled }
                                Button={
                                    <Button
                                        leftIcon={ isSelected ? <CheckIcon size={ 16 }/> : null }
                                        variant={ (isSelected || noSelected) ? 'primary' : 'secondary' }
                                        className={ styles.button }
                                        onClick={ () => setPlanId(plan.id) }
                                        type="button"
                                    >
                                        { isSelected ? 'Selected' : 'Select' }
                                    </Button>
                                }
                            />
                        );
                    })
                }
            </div>

            <div className={ styles.vat }>
                <FieldRequiredMark className={ styles.red_mark }/>
                <Text
                    as='span'
                    size='sm'
                    tone='secondary'
                    lineHeight={ 1 }
                >
                    Excluding VAT
                </Text>
            </div>

            <div className={ styles.vat }>
                <div>
                    <FieldRequiredMark className={ styles.red_mark }/>
                    <FieldRequiredMark className={ styles.red_mark }/>
                </div>
                <Text
                    as='span'
                    size='sm'
                    tone='secondary'
                    lineHeight={ 1 }
                >
                    All additional options in subscription plans will become available soon
                </Text>
            </div>

            {
                error &&
                <Text
                    size='sm'
                    tone='destructive'
                    className={ styles.error }
                >
                    { error }
                </Text>
            }
        </div>
    );
};
